import { useMemo } from "react";
import {
  Transforms,
  Editor,
  createEditor,
  Element,
  Text,
  Path,
  Range,
  Node as SlateNode,
} from "slate";
import { withReact, ReactEditor } from "slate-react";
import { withHistory } from "slate-history";
import {
  ELEMENT_TYPE_PARAGRAPH,
  ELEMENT_TYPE_IMAGE,
  ELEMENT_TYPE_LINK,
  EDITOR_OPTION_LINK,
} from "./InlineEditorConstants";
import _ from "lodash";
import { getHasHtmlCode } from "Utils/StringHelpers";
import { generateRandomIdV2 } from "Utils";

const dataURLtoBlob = dataUrl => {
  var arr = dataUrl.split(","),
    mime = arr[0].match(/:(.*?);/)[1],
    bstr = atob(arr[1]),
    n = bstr.length,
    u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }

  return new Blob([u8arr], { type: mime });
};

const withImages = editor => {
  const { insertData, isVoid } = editor || {};

  editor.isVoid = element => {
    return element.type === ELEMENT_TYPE_IMAGE ? true : isVoid(element);
  };

  editor.insertData = data => {
    const { files } = data;

    if (files && files.length > 0) {
      for (const file of files) {
        const reader = new FileReader();
        const [mime] = file.type.split("/");

        if (mime === "image") {
          reader.addEventListener("load", () => {
            const blob = dataURLtoBlob(reader.result);
            const type = blob.type;
            const blobUrl = URL.createObjectURL(blob);

            const updatedFile = new File([blob], file.name, { type });
            updatedFile.preview = blobUrl;

            insertImage({
              editor,
              url: blobUrl,
              file,
            });
          });

          reader.readAsDataURL(file);
        }
      }
    } else {
      insertData(data);
    }
  };

  return editor;
};
const withLinkHandling = editor => {
  const { insertText } = editor;

  editor.insertText = text => {
    const { selection } = editor;

    if (selection) {
      const [match] = Editor.nodes(editor, {
        at: selection,
        match: n =>
          !Editor.isEditor(n) &&
          Element.isElement(n) &&
          n.type === ELEMENT_TYPE_LINK,
      });

      if (match) {
        const [linkNode, path] = match;
        const { anchor } = selection;

        if (Editor.isEnd(editor, anchor, path)) {
          // Check if there's a text node right after the link
          const nextPath = Path.next(path);
          const nextNode = SlateNode.get(editor, nextPath);

          // If the next node is a text node, append text to it
          if (Text.isText(nextNode)) {
            Transforms.insertText(editor, text, {
              at: Editor.end(editor, nextPath),
            });
          } else {
            // If there's no text node, insert a new text node
            Transforms.insertNodes(editor, { text }, { at: nextPath });
          }

          // Move the selection to the end of the inserted text
          const endOfInsertedText = Editor.end(editor, nextPath);
          Transforms.select(editor, endOfInsertedText);
          return;
        } else if (Editor.isStart(editor, anchor, path)) {
          // If inserting text before the link, insert inline text within the same block
          Transforms.insertText(editor, text, { at: path });

          // Keep the selection at the correct position
          Transforms.select(editor, anchor);
          return;
        }
      }
    }

    // Default behavior
    insertText(text);
  };

  return editor;
};

const addExtraPlugins = ({ plugins, editorToolbarOptions }) => {
  if (_.includes(editorToolbarOptions, EDITOR_OPTION_LINK)) {
    return {
      ...plugins,
      links: true,
    };
  }
  return plugins;
};

export const addExtraPluginsMemoized = _.memoize(
  params => addExtraPlugins(params),
  ({ plugins, editorToolbarOptions }) => {
    const hasEditorLink = _.includes(editorToolbarOptions, EDITOR_OPTION_LINK);
    const pluginsData = {
      images: _.get(plugins, "images", false),
      links: hasEditorLink ? true : _.get(plugins, "links", false),
    };

    return JSON.stringify({ plugins: pluginsData });
  }
);

export const useEditor = ({ plugins }) => {
  return useMemo(() => {
    let editor = withReact(withHistory(createEditor()));

    const { isInline } = editor;
    editor.isInline = el => {
      return (
        (Element.isElement(el) && el.type === ELEMENT_TYPE_LINK) || isInline(el)
      );
    };

    if (_.get(plugins, "images", false)) {
      editor = withImages(editor);
    }
    if (_.get(plugins, "links", false)) {
      editor = withLinkHandling(editor);
    }

    return editor;
  }, [plugins]);
};

export const isFormatActive = (editor, format) => {
  const marks = Editor?.marks?.(editor);

  return marks ? marks[format] === true : false;
};

export const toggleFormat = (editor, format) => {
  const isActive = isFormatActive(editor, format);

  if (isActive) {
    Editor.removeMark(editor, format);
  } else {
    Editor.addMark(editor, format, true);
  }
};

const insertImage = async ({ editor, url, file }) => {
  const text = { text: "" };
  const image = {
    id: generateRandomIdV2(),
    type: ELEMENT_TYPE_IMAGE,
    url,
    children: [text],
    __source: { file },
  };

  Transforms.insertNodes(editor, image);
  Transforms.insertNodes(editor, {
    type: ELEMENT_TYPE_PARAGRAPH,
    children: [{ text: " " }],
  });
};

export const insertOrUpdateLink = ({
  editor,
  url,
  displayText,
  linkElement,
}) => {
  if (!url || !displayText) return;

  if (linkElement) {
    // Update the existing link element
    const path = ReactEditor.findPath(editor, linkElement);
    Transforms.setNodes(editor, { url }, { at: path });

    // Replace the existing text inside the link element
    Transforms.delete(editor, { at: [...path, 0] });
    Transforms.insertText(editor, displayText, { at: [...path, 0] });
  } else {
    const { selection } = editor;
    if (!selection || Range.isCollapsed(selection)) return;

    // Collect only inline nodes within the selection
    const inlineNodes = Array.from(
      Editor.nodes(editor, {
        at: selection,
        match: n =>
          Text.isText(n) ||
          (Element.isElement(n) && !Editor.isBlock(editor, n)),
      })
    );

    // Check for link and non-link elements in the selection
    const hasLinkElement = inlineNodes.some(([node]) => {
      return Element.isElement(node) && node.type === ELEMENT_TYPE_LINK;
    });

    const hasNonLinkElement = inlineNodes.some(([node, path]) => {
      if (Text.isText(node)) {
        const [parent] = Editor.parent(editor, path);
        return !(
          Element.isElement(parent) && parent.type === ELEMENT_TYPE_LINK
        );
      }
      return Element.isElement(node) && node.type !== ELEMENT_TYPE_LINK;
    });

    const hasMixedElements = hasLinkElement && hasNonLinkElement;

    const convertWholeSelectionToLink = () => {
      // Unwrap all link elements and other inline elements in the selection
      Transforms.unwrapNodes(editor, {
        at: selection,
        match: n => Element.isElement(n) && !Editor.isBlock(editor, n),
        split: true,
      });
      const { selection: newSelection } = editor;
      // Now, delete the content in the selection
      Transforms.delete(editor, { at: newSelection });

      // Create the new link element with the specified URL and display text
      const linkElement = {
        type: ELEMENT_TYPE_LINK,
        url,
        children: [{ text: displayText }],
      };

      Transforms.insertNodes(editor, linkElement);
    };

    if (hasMixedElements) {
      convertWholeSelectionToLink();
    } else {
      // Check if the selection already contains a link
      const [linkMatch] = Editor.nodes(editor, {
        at: selection,
        match: n =>
          !Editor.isEditor(n) &&
          Element.isElement(n) &&
          n.type === ELEMENT_TYPE_LINK,
      });

      if (linkMatch) {
        // Update the existing link element
        const [, path] = linkMatch;
        Transforms.setNodes(editor, { url }, { at: path });
        Transforms.delete(editor, { at: [...path, 0] });
        Transforms.insertText(editor, displayText, { at: [...path, 0] });
      } else {
        convertWholeSelectionToLink();
      }
    }
  }
};

export const deleteLink = ({ editor, linkElement }) => {
  // Find the path of the link element
  const path = ReactEditor.findPath(editor, linkElement);

  // Unwrap the link element, keeping its children (text and formatting) in place
  Transforms.unwrapNodes(editor, {
    at: path,
    match: n => Element.isElement(n) && n.type === ELEMENT_TYPE_LINK,
  });
  // Move the selection to the end of the editor
  Transforms.select(editor, Editor.end(editor, []));
  // Focus the editor
  ReactEditor.focus(editor);
};

const escapeHtml = text => {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
};

const serializeChildren = children => {
  return _.map(children, child => {
    // It could be possible that the child is an element
    const childType = _.get(child, "type", "");
    if (childType) {
      return serializeHTML([child]);
    }

    const childText = _.get(child, "text", "");
    let formattedChild = escapeHtml(childText);

    if (_.get(child, "bold", false)) {
      formattedChild = `<strong>${formattedChild}</strong>`;
    }
    if (_.get(child, "italic", false)) {
      formattedChild = `<em>${formattedChild}</em>`;
    }
    if (_.get(child, "underlined", false)) {
      formattedChild = `<u>${formattedChild}</u>`;
    }

    return `<span data-slate-node="text">${formattedChild}</span>`;
  }).join("");
};

export const serializeHTML = value => {
  let htmlString = "";
  _.forEach(value, element => {
    const elementType = _.get(element, "type", "");
    const children = _.get(element, "children", []);

    switch (elementType) {
      case ELEMENT_TYPE_PARAGRAPH: {
        const serializedChildren = serializeChildren(children);
        htmlString =
          htmlString + `<p data-slate-node="element">${serializedChildren}</p>`;
        break;
      }
      case ELEMENT_TYPE_LINK: {
        const linkHref = _.get(element, "url", "#");
        const serializedChildren = serializeChildren(children);
        htmlString =
          htmlString + `<a href="${linkHref}">${serializedChildren}</a>`;

        break;
      }
      case ELEMENT_TYPE_IMAGE:
        htmlString =
          htmlString +
          (element?.width
            ? `<img id='${element?.id}' src='${element?.url}' alt='${element?.url}' width='${element?.width}' />`
            : `<img id='${element?.id}' src='${element?.url}' alt='${element?.url}' />`);
        break;

      default:
        break;
    }
  });
  return htmlString;
};

function getAllNodeNames(element, nodeNames = []) {
  nodeNames.push(element.nodeName); // Add the current node's name

  // Recursively call the function on each child node
  element.childNodes.forEach(child => {
    getAllNodeNames(child, nodeNames);
  });
  return nodeNames;
}

function cleanHTML(input) {
  // Create a temporary container to hold the input HTML
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = input;

  // Select all span elements
  const spans = tempDiv.querySelectorAll("span");

  // Loop through each span element
  spans.forEach(span => {
    // Trim the text content of each span
    span.textContent = span.textContent.trim();

    // Remove empty spans
    if (!span.textContent) {
      span.remove();
    }
  });

  // Return the cleaned HTML
  return tempDiv.innerHTML;
}

const deserializeChildNodes = childNodes => {
  // Map over childNodes and flatten the results to prevent nested arrays
  const deserializedNodes = _.flatMap(childNodes, childNode => {
    const childType = childNode.nodeType;
    const nodeName = childNode.nodeName;
    if (
      childType &&
      childType !== Node.TEXT_NODE &&
      !["U", "STRONG", "EM", "BR"].includes(nodeName)
    ) {
      // Call processNodes for further deserialization
      return processNodes([childNode]);
    }
    const nodeNames = getAllNodeNames(childNode);
    const childNodeText = childNode?.textContent;

    const leafObject = {
      text: childNodeText,
    };
    if (_.includes(nodeNames, "BR")) {
      leafObject.text = "\n";
    }

    if (_.includes(nodeNames, "STRONG")) {
      leafObject.bold = true;
    }
    if (_.includes(nodeNames, "EM")) {
      leafObject.italic = true;
    }
    if (_.includes(nodeNames, "U")) {
      leafObject.underlined = true;
    }

    return leafObject;
  });

  return deserializedNodes;
};

const processNodes = nodes => {
  return _.flatMap(nodes, node => {
    const nodeName = node?.nodeName;
    const nodeText = node?.textContent;
    const childNodes = node?.childNodes;

    switch (nodeName) {
      case "P":
        return {
          type: ELEMENT_TYPE_PARAGRAPH,
          children: _.isEmpty(childNodes)
            ? [{ text: nodeText }]
            : deserializeChildNodes(childNodes),
        };
      case "SPAN":
        return _.isEmpty(childNodes)
          ? { text: nodeText }
          : deserializeChildNodes(childNodes);
      case "A": {
        const linkHref = node?.getAttribute("href") || "#";
        const deserializedChildren = _.isEmpty(childNodes)
          ? [{ text: nodeText }]
          : deserializeChildNodes(childNodes);
        return {
          type: "link",
          url: linkHref,
          children: deserializedChildren,
        };
      }
      case "IMG":
        return {
          url: node?.src,
          children: [{ text: "" }],
          type: ELEMENT_TYPE_IMAGE,
          ...(node?.id && { id: node?.id }),
          ...(node?.width && { width: node?.width }),
        };
      default:
        // added to be defensive
        return {
          type: ELEMENT_TYPE_PARAGRAPH,
          children: [{ text: "" }],
        };
    }
  });
};

export const deserializeHTML = ({ value, trimValue }) => {
  const isHTML = getHasHtmlCode({ message: value });

  if (!isHTML) {
    return [
      {
        type: ELEMENT_TYPE_PARAGRAPH,
        children: [{ text: value }],
      },
    ];
  }

  const parsed = new DOMParser().parseFromString(
    trimValue ? cleanHTML(value) : value,
    "text/html"
  );

  const allNodes = parsed?.body?.childNodes;

  if (_.isEmpty(allNodes)) {
    return [
      {
        type: ELEMENT_TYPE_PARAGRAPH,
        children: [{ text: "" }],
      },
    ];
  }

  const deserializedValue = processNodes(allNodes);
  return deserializedValue;
};
